import React, { useState, useRef, useEffect } from 'react';
import { FiInfo } from 'react-icons/fi';

/**
 * Reusable page header with title and optional actions.
 * Description and "What you can do" / "What you should do" are shown in a
 * hover-triggered info popover (info icon next to title).
 *
 * @param {string} title - Page title (rendered as h1)
 * @param {string} [description] - One or two sentences (shown in info popover)
 * @param {string[]} [whatYouCanDo] - Bullets for "What you can do" (in popover)
 * @param {string[]} [whatYouShouldDo] - Bullets for "What you should do" (in popover)
 * @param {React.ReactNode} [actions] - Optional right-aligned action (e.g. button)
 */
const PageHeader = ({
  title,
  description,
  whatYouCanDo = [],
  whatYouShouldDo = [],
  actions,
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const timeoutRef = useRef(null);
  const hasInfo = description || (whatYouCanDo?.length > 0) || (whatYouShouldDo?.length > 0);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowInfo(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShowInfo(false), 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="flex-1 min-w-0 flex items-start gap-2">
          <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
            {title}
          </h1>
          {hasInfo && (
            <div
              className="relative flex-shrink-0 mt-1"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                aria-label="Page information"
              >
                <FiInfo className="h-5 w-5" />
              </button>
              {showInfo && (
                <div
                  className="absolute left-0 top-full mt-1 z-50 w-80 max-w-[calc(100vw-2rem)] p-4 bg-white border border-gray-200 rounded-lg shadow-lg text-left"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {description && (
                    <p className="text-sm text-gray-600 mb-3">
                      {description}
                    </p>
                  )}
                  <div className="grid grid-cols-1 gap-3 text-sm text-gray-600">
                    {whatYouCanDo?.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">What you can do</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {whatYouCanDo.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {whatYouShouldDo?.length > 0 && (
                      <div>
                        <p className="font-medium text-gray-700 mb-1">What you should do</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {whatYouShouldDo.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {actions && (
          <div className="flex-shrink-0">{actions}</div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
